/**
 * The view-model shapes the designed cards take as props.
 *
 * These lived in `maintenanceRelationships.ts` alongside the v1 mock derivation, which made every
 * designed component look like a v1 dependency when all it wanted was a prop type. Extracted here so
 * the v1 file could be deleted: a card takes one of these, and whoever renders it maps the plan node
 * to it (see `model/planNodeFacts.ts`).
 *
 * Deliberately narrow and with no way back to a domain type — a card that draws a name and a role
 * has no business reaching for the node it came from.
 */
import type { PlanAttachmentOut } from '@/client';

/** A document a card was derived from. Mapped from `provenance.source_refs`. */
export interface MaintenanceSource {
	documentId: number;
	documentPublicId: string;
	filename: string;
	kind?: 'document' | 'warranty';
	page?: number;
	quote?: string;
}

/**
 * The party who can action a warranty — usually whoever issued it. Held on the warranty task
 * because the warranty document is what names them; tasks that merely reference a warranty resolve
 * it through that relationship rather than repeating it.
 *
 * Mapped from the first `system.service_contacts` entry. `contactId` is absent while the provider
 * has no saved contact record, which is the common case today.
 */
export interface MaintenanceProvider {
	name: string;
	role?: string;
	phone?: string;
	email?: string;
	status?: 'confirmed' | 'candidate' | 'unreviewed';
	candidateId?: number;
	contactId?: number;
}

/**
 * Who did a piece of work, in the three ways the app can know it.
 *
 * `name` is a display copy in every case — the id is what reconciles, so a renamed user or a
 * revoked grant still resolves. `unregistered` is deliberately the only shape that can't be
 * reconciled, which is what makes it visible as a claim rather than a record.
 */
export type MaintenanceActor =
	| { kind: 'user'; userId: number; name: string }
	| { kind: 'provider'; providerName: string; contactId?: number }
	| { kind: 'unregistered'; name: string }
	/**
	 * Somebody did this and the record cannot name them.
	 *
	 * Distinct from an absent actor, which means Bundle acted. An occurrence names its actor by
	 * `user_id`, and an id belonging to somebody no longer on the house resolves to no name — so
	 * this shape is what a withdrawn member's work reads as, rather than the app's.
	 */
	| { kind: 'unknown' };

export type MaintenanceActivityLink =
	| { kind: 'completion'; completionId: number }
	| { kind: 'task'; taskId: number }
	/**
	 * One dated instance of a card, addressed the only way an occurrence can be: by the card it
	 * belongs to and the date it fell on. It carries no id of its own that survives a reprocess, and
	 * a group's rolled-up entry has no id at all.
	 */
	| { kind: 'occurrence'; nodeId: string; date: string };

/**
 * What has happened to a task, oldest event last.
 *
 * `actor` is absent when Bundle did it rather than a person, which is how a derived task's opening
 * entry differs from a composer task's.
 */
export const MaintenanceActivityKind = {
	Added: 'added',
	Rescheduled: 'rescheduled',
	FrequencyChanged: 'frequency-changed',
	Completed: 'completed',
	Skipped: 'skipped',
	/** Deferred past its date rather than acted on. */
	Snoozed: 'snoozed',
	/** A warranty's cover ended, which is informational rather than late work. */
	Expired: 'expired',
	/** The next occurrence a completion or skip produced, which is a schedule change in its own right. */
	Scheduled: 'scheduled',
	ReminderAdded: 'reminder-added',
	NoteAdded: 'note-added',

	// Obligation-scoped verdicts. Occurrence outcomes above say what happened on a date; these say
	// what the household decided about the card itself, and only the decision log carries them.
	Kept: 'kept',
	/**
	 * A keep that put the card on the schedule, or kept it on the suggestion surface.
	 *
	 * The log records both as `keep` — placement travels in the decision's payload, and a second keep
	 * with the other placement is how a card moves between the two surfaces. Told apart here so the
	 * The payload does not carry the previous placement, so a General keep cannot honestly claim it
	 * was a demotion: it may be the card's initial review verdict.
	 */
	KeptAsPriority: 'kept-as-priority',
	KeptAsSuggestion: 'kept-as-suggestion',
	/**
	 * The household wrote this card rather than reviewing one Bundle proposed.
	 *
	 * The log records it as the same `keep` a review verdict takes, so the two are told apart by
	 * whose card it is — see {@link timelineActivityEvents}.
	 */
	Created: 'created',
	Prioritised: 'prioritised',
	Deprioritised: 'deprioritised',
	Unkept: 'unkept',
	Dismissed: 'dismissed',
	Restored: 'restored',
	Edited: 'edited',
	TitleChanged: 'title-changed',
	SummaryChanged: 'summary-changed',
	NoteChanged: 'note-changed',
	Reverted: 'reverted',
	Attached: 'attached',
	/**
	 * A verb this build does not know.
	 *
	 * `DecisionVerb` is open precisely so history written by a newer server still renders. A dropped
	 * row is a household's record going missing, which is worse than an unspecific one.
	 */
	Unknown: 'unknown',
} as const;

export type MaintenanceActivityKind = (typeof MaintenanceActivityKind)[keyof typeof MaintenanceActivityKind];

export interface MaintenanceActivityEvent {
	kind: MaintenanceActivityKind;
	/** ISO timestamp the event happened at. */
	at: string;
	/**
	 * The date the series stood at once this entry had happened.
	 *
	 * A completion's whole consequence is where it pushed the work to, and the add's is the date the
	 * card started on — see `scheduleAfterEachInstance`. Carried on the entry that caused it, which
	 * is the only thing that knows, and then lifted out into an entry of its own by
	 * `withScheduleEntries`. Nothing renders it in place: an entry reaching this far has already been
	 * expanded.
	 */
	scheduledFor?: string;
	/**
	 * Who did the work. Absent when Bundle derived the entry rather than a person acting.
	 * Frequently the same person as `recordedBy`, and only interesting when it isn't.
	 */
	actor?: MaintenanceActor;
	/**
	 * The user who entered it. Always a user when a person was involved at all: the app can only
	 * witness someone with access pressing a button, whoever they say did the work.
	 */
	recordedBy?: Extract<MaintenanceActor, { kind: 'user' }>;
	/** What changed, in the task's own terms — "12 Aug → 26 Aug", "Monthly → every 3 months". */
	detail?: string;
	/**
	 * For a schedule entry lifted out of another: when that other entry happened.
	 *
	 * Two settlements can send the series to the same day, and the account keeps both — they are two
	 * occasions on which the plan came to stand at that date. Nothing else tells the pair apart,
	 * since a schedule entry's own `at` *is* the date, so this is what gives each one an identity of
	 * its own. Absent on the card's standing date, which no entry produced.
	 */
	liftedFrom?: string;
	/** Where the entry leads. Rendered as plain text when the drawer has no handler for its kind. */
	link?: MaintenanceActivityLink;
	/**
	 * Files filed on this occasion, listed beneath the entry.
	 *
	 * A count says a household kept a receipt; this is what lets them open it. Absent on an entry
	 * that is not an occasion — a card being added files nothing.
	 */
	evidence?: PlanAttachmentOut[];
	/**
	 * What the household wrote on this occasion, in their own words.
	 *
	 * Its own field rather than `detail`, which is the app's phrasing of what changed and is written
	 * into the entry's own line. A note is quoted material of unbounded length and belongs beneath.
	 */
	note?: string;
	/**
	 * The documents an entry came out of, listed beneath it.
	 *
	 * Distinct from `evidence`, which is what an occasion *left behind* — a receipt, a photo. These
	 * are what it was *read from*, and only a card's own beginning has any: Bundle derived it from
	 * a document, and this is that document.
	 */
	sources?: MaintenanceSource[];
	/**
	 * The card's own beginning: the add Bundle derived from a document, or the household writing
	 * the card themselves. Its own flag rather than a kind, because `Added` is also what an undone
	 * completion maps to, and only the true beginning is an end of the account.
	 */
	origin?: true;
}

/**
 * Who an entry credits. The recorder is named only when it isn't the person who did the work — the
 * app can only witness whoever pressed the button, and saying so every time would drown the cases
 * where the two genuinely differ. Shared, so the log and the task's own details can't disagree
 * about who did something.
 */
/** Shown where a name would go when the record cannot supply one. */
export const UNKNOWN_ACTOR_NAME = 'Not available';

/** What to call an actor, whichever shape it takes. */
export const getActorName = (actor: MaintenanceActor): string => {
	if (actor.kind === 'unknown') return UNKNOWN_ACTOR_NAME;

	return actor.kind === 'provider' ? actor.providerName : actor.name;
};

/** Entries Bundle made rather than a person. Named, so nothing ever implies the household did it. */
export const BUNDLE_ACTOR_NAME = 'Bundle Intelligence';

export const getActivityAttribution = (event: MaintenanceActivityEvent): string => {
	if (!event.actor) return BUNDLE_ACTOR_NAME;

	const actorName = getActorName(event.actor);
	const recorderName = event.recordedBy?.name;
	const isSamePerson = event.actor.kind === 'user' && event.actor.userId === event.recordedBy?.userId;

	return !recorderName || isSamePerson ? actorName : `${actorName}, recorded by ${recorderName}`;
};
